import { BehaviorSubject, combineLatest, EMPTY, first, forkJoin, from, fromEvent, merge, Observable, of, ReplaySubject, Subject, throwError, startWith } from 'rxjs';
import { catchError, delay, distinctUntilChanged, filter, finalize, map, mergeMap, share, switchMap, tap } from 'rxjs/operators';

import { onlineNodes$, answer, gptQueue$, model, maxContextWindow } from './gpt.js';
import { userInfo$, lang$, fetchText, logout } from './http.js';
import { showError, lockControls } from './ui.js';
import { promptTemplates$, buildPrompt, buildEndOfPrompt, buildCitation, translateDom } from './prompts.js';
import { userSettings$, userSettingsUpdater$, updateUserSetting, syncInputWithSettings } from './settings.js';
import { appendChatMessage, updateConversationAttr, archiveMessage, conversationTitle$, chatMessages$, conversations$, hasTitle$ } from './conversations.js';
import { md2html } from './markdown.js';
import { nestMarkdown } from '../../backend/lib/markdown.js';
import { notificationsEnabled$, notifyAbout, notifications$ } from './notifications.js';
import { geoLocationEnabled$, currentLocation$, geonames$ } from './geonames.js';
import { researchAgent } from './research-agent.js';
import { productHuntAgent } from './product-hunt-agent.js';

let newMessageContainer = document.getElementById('new-message');
let newTaskContainer = document.querySelector('form#new-task');

let enforceInputMode$ = new Subject();

let newInputMode$ = merge(
    of('text'),
    fromEvent(newMessageContainer, 'keyup').pipe(
        filter(isToggleEvent),
        map(event => {
            event.preventDefault();
            return 'task';
        }),
    ),
    fromToggleSwipe(newMessageContainer).pipe(
        map(() => 'task'),
    ),
    fromEvent(newTaskContainer, 'keyup').pipe(
        filter(isToggleEvent),
        map(event => {
            event.preventDefault();
            return 'text';
        }),
    ),
    fromToggleSwipe(newTaskContainer).pipe(
        map(() => 'text'),
    ),
    enforceInputMode$,
);

function isToggleEvent(event) {
    return event.ctrlKey && !event.metaKey && !event.shiftKey && event.key === 'ArrowRight';
}

function fromToggleSwipe(root) {
    return fromEvent(root, 'touchstart').pipe(
        switchMap(startEvent => {
            if (startEvent.touches.length !== 1) {
                return EMPTY;
            }
            let touch = startEvent.touches[0];
            let startX = touch.screenX;
            let startY = touch.screenY;
            return fromEvent(root, 'touchend').pipe(
                switchMap(endEvent => {
                    if (endEvent.changedTouches.length !== 1) {
                        return EMPTY;
                    }
                    let touch = endEvent.changedTouches[0];
                    let endX = touch.screenX;
                    let endY = touch.screenY;
                    let dX = endX - startX;
                    let dY = endY - startY;
                    if (Math.abs(dX) <= Math.abs(dY)) {
                        return EMPTY;
                    }
                    if (Math.abs(dX) < 100) {
                        return EMPTY;
                    }
                    return of(undefined);
                }),
            );
        }),
    );
}

let sendTextEl = document.getElementById('send-text');
sendTextEl.focus();

fromEvent(document.getElementById('logout'), 'click').pipe(
    tap(() => lockControls(false)),
    switchMap(() => logout()),
)
// don't use 'big merge' with default error handling below. the
// logout should also work after an error has occured.
    .subscribe();

let offlineIndicator$ = onlineNodes$.pipe(
    tap(onlineNodes => document.getElementById('offline-indicator').classList.toggle('hidden', onlineNodes > 0)),
);

let newConversation$ = fromEvent(document.getElementById('new-conversation'), 'click').pipe(
    tap(() => {
        window.location.hash = '';
        resetUserInput();
        sendTextEl.focus();
    }),
);

let isLoading = false;

let systemPromptEl = document.querySelector('.system-prompt-container .content');
let syncCustomSystemPrompt$ = syncInputWithSettings(systemPromptEl, 'customSystemPrompt');

let syncHelpToggle$ = syncInputWithSettings(document.getElementById('help-toggle'), 'help').pipe(
    tap(help => document.body.classList.toggle('show-help', help)),
);
let syncHereAndNowToggle$ = syncInputWithSettings(document.getElementById('here-and-now-toggle'), 'hereAndNow');
let syncGeoLocation$ = syncInputWithSettings(document.getElementById('geo-location-toggle'), 'geoLocation').pipe(
    tap(enabled => geoLocationEnabled$.next(enabled)),
);
let syncInternetResearchToggle$ = syncInputWithSettings(document.getElementById('internet-research-toggle'), 'internetResearch');
let syncCollectKnowledgeToggle$ = syncInputWithSettings(document.getElementById('collect-knowledge-toggle'), 'collectKnowledge');
let syncShowNotificationsToggle$ = syncInputWithSettings(document.getElementById('show-notifications-toggle'), 'showNotifications').pipe(
    tap(showNotifications => notificationsEnabled$.next(showNotifications)),
);
let syncZeroTemperatureToggle$ = syncInputWithSettings(document.getElementById('zero-temperature-toggle'), 'zeroTemperature');

let syncDebugToggle$ = syncInputWithSettings(document.getElementById('debug-toggle'), 'debug').pipe(
    tap(debug => {
        if (debug) {
            document.body.classList.add('debug');
            document.body.classList.remove('no-debug');
        } else {
            document.body.classList.add('no-debug');
            document.body.classList.remove('debug');
        }
    }),
);

function currentChatMessages(includeSystem=true, roleFilter=null) {
    let systemPrompt$ = !includeSystem ? of(null) : of(undefined).pipe(
        switchMap(() => {
            let fragments = [];
            if (hereAndNowEnabled()) {
                fragments.push(combineLatest([
                    userInfo$,
                    lang$,
                ]).pipe(
                    switchMap(([userInfo, lang]) => buildPrompt('here&now', {
                        ...userInfo.oauth,
                        now: new Date().toLocaleString(lang),
                    })),
                ));
            }
            if (geoLocationEnabled()) {
                fragments.push(currentLocation$.pipe(
                    first(),
                    switchMap(location => {
                        if (!location) {
                            return of(null);
                        }
                        return buildPrompt('location', {
                            ...location,
                        });
                    }),
                ));
            }
            fragments.push(of(systemPromptEl.value));
            return forkJoin(fragments);
        }),
        map(fragments => fragments.filter(frag => !!frag).join(' ')),
    );
    return forkJoin([
        systemPrompt$,
        chatMessages$.pipe(first()),
    ]).pipe(
        map(([systemPrompt, chatMessages]) => {
            let messages = [];
            if (systemPrompt) {
                messages.push({
                    id: 'system',
                    role: 'system',
                    content: systemPrompt,
                });
            }
            return messages.concat(chatMessages);
        }),
        map(messages => {
            if (!roleFilter) {
                return messages;
            }
            let roleSet = new Set(roleFilter);
            return messages.filter(msg => roleSet.has(msg.role));
        }),
    );
}

let newPlainMessage$ = fromEvent(document.getElementById('send-button'), 'click').pipe(
    map(() => sendTextEl.value),
);

let newTaskMessage$ = fromEvent(newTaskContainer, 'submit').pipe(
    switchMap(event => {
        event.preventDefault();
        return promptTemplates$.pipe(
            first(),
            map(translations => {
                let newTaskContainer = document.querySelector('#new-task');
                let task = getValue(newTaskContainer, '.task');
                let sections = Array.from(newTaskContainer.querySelectorAll('.section'))
                    .map(sectionEl => ({
                        title: getValue(sectionEl, '.section-headline') || translations['section-input'] || 'Input',
                        content: getValue(sectionEl, '.section-content'),
                    }))
                    .filter(section => section.content);
                let response = getValue(newTaskContainer, '.response') || translations['response'] || 'Response';
                let fragments = [
                    `# ${translations.task || 'Task'}\n\n`,
                    task,
                    '\n\n',
                ];
                for (let section of sections) {
                    fragments.push(`## ${section.title}\n\n`);
                    fragments.push(section.content);
                    fragments.push('\n\n');
                }
                fragments.push(`## ${response}\n\n`);
                return fragments.join('');

                function getValue(parent, selector) {
                    let el = parent.querySelector(selector);
                    return (el.value || '').trim();
                }
            }),
        );
    }),
);

let newMessage$ = merge(newPlainMessage$, newTaskMessage$).pipe(
    tap(msg => {
        resetUserInput();
        sendTextEl.focus();
        appendChatMessage({
            role: 'user',
            content: msg,
        });
        scrollChatToBottom();
    }),
);

let agentApi = {
    collectKnowledgeEnabled,
    internetResearchEnabled,
    zeroTemperatureEnabled,

    switchAgent,

    currentChatMessages,
    findLastMessageForRole,
    answerTask,
    answerJson,

    appendChatMessage,
};

let agents = {
    '': researchAgent,
    'research': researchAgent,
    'product-hunt': productHuntAgent,
};

function switchAgent(name, msg) {
    return of(undefined).pipe(
        switchMap(() => {
            if (!agents.hasOwnProperty(name)) {
                throw new Error(`Unable to switch to unknown agent: ${name}`);
            }
            let agent = agents[name];
            appendChatMessage({
                role: 'switch-agent',
                content: name,
            });
            return agent(this, msg);
        }),
    );
}

function findLastMessageForRole(role) {
    return currentChatMessages().pipe(
        map(messages => {
            for (let i = messages.length - 1; i >= 0; --i) {
                let msg = messages[i];
                if (msg.role === role) {
                    return msg;
                }
            }
            return null;
        }),
    );
}

function answerTask(promptName, vars, opts={}) {
    return currentChatMessages(true, ['system', 'user', 'assistant']).pipe(
        first(),
        switchMap(messages => {
            if (messages[messages.length - 1].role !== 'user') {
                throw new Error('Tasks can only be answered after a user request.');
            }
            let lastUserMessage = nestMarkdown(messages[messages.length - 1].content);
            let allVars = {
                lastUserMessage,
                ...vars,
            };
            return buildPrompt(promptName, allVars).pipe(
                switchMap(prompt => {
                    // the .slice drops the last user message from the
                    // context. we assume that a task that is
                    // interested in the last user messages uses the
                    // lastUserMessage variable in it's prompt
                    // template.
                    let taskMessages = messages.slice(0, messages.length - 1);
                    taskMessages.push({
                        role: 'user',
                        content: prompt,
                    });
                    return answer(taskMessages, {
                        ...opts,
                        options: {
                            ...opts.options || {},
                            temperature: zeroTemperatureEnabled() ? 0 : 1,
                        },
                        balancer_options: {
                            security_level: 100,
                            ...opts.balancer_options || {},
                        },
                    });
                }),
                map(resp => resp.message.content),
            );
        }),
    );
}

function answerJson(promptName, vars, format, opts={}) {
    return answerTask(promptName, vars, {
        ...opts,
        options: {
            temperature: 1,
            ...opts.options || {},
        },
        format,
    }).pipe(
        map(msg => JSON.parse(msg)),
    );
}

let incommingMessage$ = newMessage$.pipe(
    tap(() => indicateLoading(true)),
    switchMap(msg => findLatestAgentName().pipe(
        switchMap(agentName => {
            let agent = agents[agentName || ''] || researchAgent;
            return agent(agentApi, msg);
        }),
    )),
    share({
        connector: () => new ReplaySubject(1),
        resetOnRefCountZero: true,
        resetOnError: true,
        resetOnComplete: true,
    }),
);

function findLatestAgentName() {
    return currentChatMessages().pipe(
        first(),
        map(messages => {
            for (let i = messages.length - 1; i >= 0; --i) {
                let msg = messages[i];
                if (msg.role === 'switch-agent') {
                    return msg.content;
                }
            }
            return null;
        }),
    );
}

function hereAndNowEnabled() {
    return document.getElementById('here-and-now-toggle').checked;
}

function geoLocationEnabled() {
    return document.getElementById('geo-location-toggle').checked;
}

function internetResearchEnabled() {
    return document.getElementById('internet-research-toggle').checked;
}

function collectKnowledgeEnabled() {
    return document.getElementById('collect-knowledge-toggle').checked;
}

function zeroTemperatureEnabled() {
    return document.getElementById('zero-temperature-toggle').checked;
}

let appendAssistantChatMessages$ = incommingMessage$.pipe(
    messages$ => {
        let content = [];
        return messages$.pipe(
            switchMap(msg => {
                if (msg.init) {
                    return EMPTY;
                }
                if (msg.done) {
                    notifyAbout(document.title);
                    let res = of({
                        ...msg,
                        message: {
                            ...msg.message,
                            content: content.join('') + msg.message.content,
                            model,
                        },
                    });
                    content = [];
                    return res;
                }
                content.push(msg.message.content);
                return EMPTY;
            }),
        );
    },
    tap(msg => appendChatMessage(msg.message)),
);

let renderChatMessages$ = chatMessages$.pipe(
    switchMap(messages => {
        if (messages.length === 0) {
            return of([]);
        }
        let obs$ = [];
        let currentAgent = 'research';
        for (let msg of messages) {
            if (msg.type === 'switch-agent') {
                currentAgent = msg.content;
            }
            obs$.push(renderMessage(msg, currentAgent));
        }
        return forkJoin(obs$);
    }),
    tap(messages => {
        let autoScroll = autoScrollToBottom();
        let containerEl = document.getElementById('complete-messages');
        containerEl.innerHTML = '';
        for (let i = 0; i < messages.length; ++i) {
            let msg = messages[i];
            if (!msg) {
                continue;
            }
            containerEl.appendChild(msg);
        }
        autoScroll();
    }),
);

let renderCraftingChatMessage$ = incommingMessage$.pipe(
    tap(msg => {
        let autoScroll = autoScrollToBottom();
        let container = document.getElementById('crafting-message');
        let contentEl = container.querySelector('.content');
        if (msg.init) {
            container.style.display = 'flex';
            contentEl.textContent = '';
        } else if (msg.done) {
            indicateLoading(false);
            container.style.display = 'none';
            contentEl.textContent = '';
        } else {
            let roleEl = container.querySelector('.role');
            roleEl.textContent = msg.message.role;
            contentEl.textContent += msg.message.content;
        }
        autoScroll();
    }),
);

function renderMessage(msg, agentName) {
    switch(msg.role) {
    default:
        return of(null);
    case 'user':
    case 'assistant':
        return renderChatMessage(msg, agentName);
    case 'categorization':
        return renderCategorizationMessage(msg);
    case 'knowledge':
        return renderKnowledge(msg);
    case 'internet-research':
        return renderInternetResearch(msg);
    case 'paper-research':
        return renderPaperResearch(msg);
    case 'product-hunt-criteria':
        return renderProductHuntCriteria(msg);
    }
}

let roleLabels$ = combineLatest([
    userInfo$,
    lang$,
]).pipe(
    map(([userInfo, lang]) => {
        let labels = {
            user: userInfo.oauth.given_name || 'user',
            agentNames: {
                'product-hunt': 'Anya Kaufmann',
                'research': 'Clara Cognito',
            },
        };
        if (lang === 'de') {
            labels.agents = {
                'product-hunt': 'Anya Kaufmann',
                'research': 'Clara Cognito',
            };
        } else {
            labels.agents = {
                'product-hunt': 'Anya Lister',
                'research': 'Clara Cognito',
            };
        }
        return labels;
    }),
    share({
        connector: () => new ReplaySubject(1),
        resetOnComplete: false,
        resetOnRefCountZero: false,
        resetOnError: true,
    }),
);

function renderChatMessage(msg, agentName) {
    let formattedMsg$ = of(undefined).pipe(
        switchMap(roleLabels => {
            let reasoningPattern = /^[\s]*<think>/;
            let reasoningAnswer = msg.role === 'assistant' && reasoningPattern.exec(msg.content);
            let reasoning, answer;
            if (reasoningAnswer) {
                let thinkStart = msg.content.indexOf('<think>');
                let thinkEnd = msg.content.indexOf('</think>');
                if (thinkStart !== -1 && thinkEnd !== -1 && thinkStart < thinkEnd) {
                    reasoning = msg.content.substring(thinkStart + '<think>'.length, thinkEnd).trim();
                    if (reasoning.length === 0) {
                        reasoning = null;
                    }
                    answer = msg.content.substring(thinkEnd + '</think>'.length).trim();
                } else {
                    reasoning = null;
                    answer = msg.content;
                }
            } else {
                reasoning = null;
                answer = msg.content;
            }
            return forkJoin([
                reasoning === null ? of(null) : md2html(reasoning),
                md2html(answer),
            ]);
        }),
    );
    
    return forkJoin([
        formattedMsg$,
        roleLabels$.pipe(first()),
    ]).pipe(
        map(([[thinkHtml, answerHtml], roleLabels]) => {
            let container = document.createElement('div');
            container.classList.add('message');
            container.classList.add(`chat-role-${msg.role}`);
            let roleEl = document.createElement('div');
            roleEl.classList.add('role');
            let roleLabel;
            if (msg.role === 'assistant') {
                roleLabel = roleLabels.agents[agentName] || msg.role;
            } else {
                roleLabel = roleLabels[msg.role] || msg.role;
            }
            roleEl.textContent = roleLabel;
            container.appendChild(roleEl);
            let contentEl = document.createElement('div');
            contentEl.classList.add('content');
            if (thinkHtml === null) {
                contentEl.innerHTML = answerHtml;
            } else {
                let thinkEl = document.createElement('div');
                thinkEl.classList.add('think');
                thinkEl.innerHTML = thinkHtml;
                contentEl.appendChild(thinkEl);
                let answerEl = document.createElement('div');
                answerEl.classList.add('answer');
                answerEl.innerHTML = answerHtml;
                contentEl.appendChild(answerEl);
            }
            container.appendChild(contentEl);
            let actionsEl = document.createElement('div');
            actionsEl.classList.add('actions');
            let copyBtn = addAction('📋', 'copy');
            copyBtn.classList.add('sturdy');
            copyBtn.addEventListener('click', () => navigator.clipboard.writeText(msg.content), false);
            if (msg.role === 'user') {
                let editBtn = addAction('📝', 'edit');
                editBtn.classList.add('disabled-on-loading');
                editBtn.disabled = isLoading;
                editBtn.addEventListener('click', () => editMessage(msg.id), false);
            }
            container.appendChild(actionsEl);
            return container;

            function addAction(label, title) {
                let btn = document.createElement('button');
                btn.type = 'button';
                btn.textContent = label;
                btn.title = title;
                actionsEl.appendChild(btn);
                return btn;
            }
        }),
    );
}

function editMessage(msgId) {
    return archiveMessage(msgId).pipe(
        tap(archivedMessage => {
            resetUserInput();
            sendTextEl.value = archivedMessage.content;
            sendTextEl.focus();
        }),
        delay(0),
        tap(() => scrollChatToBottom()),
        handleErrors(),
    ).subscribe();
}

function resetUserInput() {
    enforceInputMode$.next('text');
    sendTextEl.value = '';
    let newTaskSectionsContainer = document.querySelector('#new-task .new-task-sections');
    while (newTaskSectionsContainer.firstElementChild !== newTaskSectionsContainer.lastElementChild) {
        newTaskSectionsContainer.removeChild(newTaskSectionsContainer.lastElementChild);
    }
    for (let input of document.querySelectorAll('#new-task input, #new-task textarea')) {
        input.value = '';
    }
}

function renderCategorizationMessage(msg) {
    return of(undefined).pipe(
        map(() => {
            let cat = msg.content;
            let container = document.createElement('div');
            container.classList.add('categorization-message');
            for (let query of cat.internetResearchSearchQueries || []) {
                let el = document.createElement('a');
                el.classList.add('internet-research-flag');
                el.href = `https://duckduckgo.com/?t=ffab&q=${encodeURIComponent(query)}&ia=web`;
                el.target = '_blank';
                el.textContent = `🌍 ${query}`;
                container.appendChild(el);
            }
            for (let query of cat.scientificPaperSearchTerms || []) {
                let el = document.createElement('a');
                el.classList.add('paper-research-flag');
                el.href = `https://doaj.org/search/articles?ref=homepage-box&source=%7B%22query%22%3A%7B%22query_string%22%3A%7B%22query%22%3A%22${encodeURIComponent(query)}%22%2C%22default_operator%22%3A%22AND%22%2C%22default_field%22%3A%22bibjson.abstract%22%7D%7D%2C%22track_total_hits%22%3Atrue%7D`;
                el.target = '_blank';
                el.textContent = `🔬 ${query}`;
                container.appendChild(el);
            }
            for (let topic of cat.topics || []) {
                let el = document.createElement('span');
                el.textContent = `💡 ${topic}`;
                container.appendChild(el);
            }
            if (container.children.length === 0) {
                return null;
            }
            let debug = document.createElement('pre');
            debug.classList.add('categorization-json');
            let code = document.createElement('code');
            debug.appendChild(code);
            code.textContent = JSON.stringify(cat, null, '\t');
            container.appendChild(debug);
            return container;
        }),
    );
}

function renderKnowledge(msg) {
    return of(undefined).pipe(
        switchMap(() => md2html(msg.content)),
        map(knowledgeHtml => {
            let container = document.createElement('div');
            container.classList.add('knowledge');
            let headEl = document.createElement('div');
            headEl.textContent = '💡';
            container.appendChild(headEl);
            let extractEl = document.createElement('div');
            extractEl.innerHTML = knowledgeHtml;
            container.appendChild(extractEl);
            return container;
        }),
    );
}

function renderInternetResearch(msg) {
    return of(undefined).pipe(
        switchMap(() => md2html(msg.content.extract)),
        map(extractHtml => {
            let container = document.createElement('div');
            container.classList.add('internet-research');
            let sourceEl = document.createElement('a');
            sourceEl.textContent = '🌍 ' + msg.content.href;
            sourceEl.target = '_blank';
            sourceEl.href = msg.content.href;
            container.appendChild(sourceEl);
            let extractEl = document.createElement('span');
            extractEl.innerHTML = extractHtml;
            container.appendChild(extractEl);
            return container;
        }),
    );
}

function renderPaperResearch(msg) {
    return of(undefined).pipe(
        switchMap(() => md2html(msg.content.extract)),
        map(extractHtml => {
            let container = document.createElement('div');
            container.classList.add('paper-research');
            // TODO make link optional
            let sourceEl = document.createElement('a');
            sourceEl.textContent = '🔬 ' + msg.content.title;
            sourceEl.target = '_blank';
            sourceEl.href = msg.content.href;
            container.appendChild(sourceEl);
            let extractEl = document.createElement('span');
            extractEl.innerHTML = extractHtml;
            container.appendChild(extractEl);
            return container;
        }),
    );
}

function renderProductHuntCriteria(msg) {
    return of(undefined).pipe(
        switchMap(() => {
            let productNameHtml$ = md2html(msg.content.productName);
            let acceptanceCriteriasHtml$ = msg.content.acceptanceCriteria.map(ac => md2html(ac));
            return forkJoin([
                productNameHtml$,
                acceptanceCriteriasHtml$.length === 0 ? of([]) : forkJoin(acceptanceCriteriasHtml$),
            ]);
        }),
        map(([productNameHtml, acceptanceCriteriasHtml]) => {
            let container = document.createElement('div');
            container.classList.add('product-hunt-criteria');
            let headEl = document.createElement('div');
            headEl.classList.add('head');
            let headIcon = document.createElement('span');
            headIcon.textContent = '🛍️';
            headEl.appendChild(headIcon);
            let headLabel = document.createElement('span');
            headLabel.innerHTML = productNameHtml;
            headEl.appendChild(headLabel);
            container.appendChild(headEl);
            let acUl = document.createElement('ul');
            for (let acHtml of acceptanceCriteriasHtml) {
                let li = document.createElement('li');
                li.innerHTML = acHtml;
                acUl.appendChild(li);
            }
            container.appendChild(acUl);
            return container;
        }),
    );
}

let summarizeTitle$ = of(undefined).pipe(
    switchMap(() => hasTitle$),
    switchMap(hasTitle => {
        if (hasTitle) {
            return EMPTY;
        }
        return chatMessages$.pipe(
            map(messages => {
                let chatRoles = new Set(['user', 'assistant']);
                return messages.filter(msg => chatRoles.has(msg.role));
            }),
            filter(messages => messages.length > 0),
            map(messages => messages[0].content),
            distinctUntilChanged(),
            switchMap(msg => forkJoin([
                // the delay should give the actual chat a chance to issue
                // it's request first
                of(msg).pipe(delay(300)),
                buildEndOfPrompt('TEXT').pipe(first()),
                buildPrompt('build-title', { section: 'TEXT' }).pipe(first()),
            ])),
            switchMap(([msg, endOf, buildTitle]) => {
                let messages = [
                    {
                        role: 'user',
                        content: [
                            msg,
                            endOf,
                            buildTitle,
                        ].join('\n\n'),
                    },
                ];
                return answer(messages, {
                    balancer_options: {
                        security_level: 100,
                    },
                    format: {
                        type: 'object',
                        properties: {
                            title: {
                                type: 'string',
                            },
                        },
                        required: [
                            'title',
                        ],
                    },
                });
            }),
            map(msg => {
                let obj;
                try {
                    obj = JSON.parse(msg.message.content);
                } catch (err) {
                    console.warn('Unable to parse conversation title object from content:', msg?.message?.content);
                    return null;
                }
                return obj['title'];
            }),
            distinctUntilChanged(),
            tap(title => {
                if (title) {
                    title = title.trim();
                }
                if (!title) {
                    console.warn('Unable to generate title for conversation');
                    return;
                }
                updateConversationAttr({
                    key: 'title',
                    value: title,
                });
                document.title = title;
            }),
        );
    }),
);

let loadedTitle$ = conversationTitle$.pipe(
    tap(title => {
        document.title = title || 'gpt.ma300k.de';
    }),
);

let newMessageModeContainerMap = new Map([
    ['text', newMessageContainer],
    ['task', newTaskContainer],
]);
let toggleInputModeElements$ = newInputMode$.pipe(
    distinctUntilChanged(),
    tap(mode => {
        let active = newMessageModeContainerMap.get(mode);
        for (let container of newMessageModeContainerMap.values()) {
            if (container === active) {
                container.classList.remove('hidden');
            } else {
                container.classList.add('hidden');
            }
        }
        active.querySelector('input, textarea').focus();
        active.scrollIntoView(true);
    }),
);

let addNewTaskSection$ = fromEvent(newTaskContainer.querySelector('.add-section'), 'click').pipe(
    tap(event => {
        event.preventDefault();
        let sectionsContainer = newTaskContainer.querySelector('.new-task-sections');
        let template = sectionsContainer.querySelector('.section');
        let clone = template.cloneNode(true);
        for (let input of clone.querySelectorAll('input, textarea')) {
            input.value = '';
        }
        sectionsContainer.appendChild(clone);
        clone.scrollIntoView(true);
        clone.querySelector('input, textarea').focus();
    }),
);

let translateDocument$ = translateDom(document);

merge(
    gptQueue$,
    offlineIndicator$,
    newConversation$,
    userSettingsUpdater$,
    syncCustomSystemPrompt$,
    syncHelpToggle$,
    syncHereAndNowToggle$,
    syncGeoLocation$,
    syncInternetResearchToggle$,
    syncCollectKnowledgeToggle$,
    syncShowNotificationsToggle$,
    syncZeroTemperatureToggle$,
    syncDebugToggle$,
    renderChatMessages$,
    appendAssistantChatMessages$,
    renderCraftingChatMessage$,
    summarizeTitle$,
    loadedTitle$,
    conversations$,
    notifications$,
    geonames$,
    toggleInputModeElements$,
    addNewTaskSection$,
    translateDocument$,
).pipe(
    handleErrors(),
).subscribe();

function handleErrors() {
    return catchError(err => {
        showError(err);
        indicateLoading(false);
        return EMPTY;
    });
}

function renderOptions(select) {
    return tap(options => {
        select.innerHTML = '';
        for (let o of options) {
            let oEl = document.createElement('option');
            oEl.value = o.value;
            oEl.textContent = o.label;
            select.appendChild(oEl);
        }
        indicateLoading(false);
    });
}

function indicateLoading(loading) {
    isLoading = loading;
    let autoScroll = autoScrollToBottom();
    document.getElementById('loading').style.display = loading ? 'block' : 'none';
    for (let el of document.querySelectorAll('.disabled-on-loading')) {
        el.disabled = loading;
    }
    autoScroll();
}

function autoScrollToBottom() {
    if (isChatScrolledToBottom()) {
        return scrollChatToBottom;
    }
    return () => {};
}

function isChatScrolledToBottom() {
    let scrollEl = document.getElementById('old-messages');
    return scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 10;
}

function scrollChatToBottom() {
    setTimeout(() => {
        let scrollEl = document.getElementById('old-messages');
        scrollEl.scrollTop = scrollEl.scrollHeight - scrollEl.clientHeight;
    }, 0);
}
